import {
  always,
  anyPass,
  applySpec,
  complement,
  converge,
  either,
  evolve,
  filter,
  identity,
  ifElse,
  isEmpty,
  isNil,
  map,
  mergeAll,
  of,
  path,
  pick,
  pickBy,
  pipe,
  prop,
  reject,
  sort,
  test,
  where,
  unapply,
  trim,
} from "ramda";
import {
  getWorkType,
  getContributors,
  getPrimaryCategory,
  getProjectId,
  getSecondaryCategory,
  getStepAcceptedOptIns,
  getStepFields,
  getTertiaryCategory,
  getSubmitterIsCreator,
  getSubmissionFiles,
} from "common/selectors";
import * as steps from "common/steps";
import { isBlank } from "../common/general";
import { mapOrders } from "../file-details/components/EditAlbumTracks";

const notNilOrEmpty = complement(anyPass([isEmpty, isNil]));
const pickNonEmptyFields = pickBy(notNilOrEmpty);
const removeEmptyAndEvolve = spec => pipe(pickNonEmptyFields, evolve(spec));

const mapProjectInfo = pipe(
  applySpec({
    projectId: getProjectId,
  }),
  pickNonEmptyFields
);

const getSubmitterData = state => {
  console.log("Getting submitter data");
  return getSubmitterIsCreator(state)
    ? getStepFields(steps.BASIC_DETAILS)(state)
    : getStepFields(steps.INTRO)(state);
};

const mapSubmitter = pipe(
  getSubmitterData,
  applySpec({
    firstName: prop("firstName"),
    middleName: prop("middleName"),
    lastName: prop("lastName"),
    email: prop("email"),
    localLibraryId: path(["localLibrary", "organizationId"]),
    localLibraryOther: prop("localLibraryOther"),
    countryCode: path(["country", "code"]),
    regionCode: path(["region", "code"]),
    locality: prop("locality"),
    freeformPersonalData: prop("freeformPersonalData"),
    freeformPersonalData2: prop("freeformPersonalData2"),
  }),
  pickNonEmptyFields,
  submitter => ({ submitter })
);

const mapOptIns = pipe(
  getStepAcceptedOptIns(steps.TERMS),
  map(optInId => ({ optInId })),
  optIns => ({ optIns })
);

const mapWorkType = state => {
  const workType = getWorkType(state);
  const type = workType.submissionType;
  const contentType = workType.contentTypeName;

  if (contentType === "AUDIO") {
    return {
      type,
      audioType: workType.subTypeName,
    };
  } else if (contentType === "OTHER_DOCUMENT" || contentType === "IMAGE") {
    return {
      type,
      mediumFormat: workType.subTypeName,
    };
  } else {
    return { type };
  }
};
const appendCover = sort((a, b) => (a.fileType === "COVER" ? 1 : -1));
const mapSubmissionFiles = state => {
  // If COVER file exists, ensure it is moved to end
  const submissionFiles = pipe(
    getSubmissionFiles,
    appendCover,
    mapOrders
  )(state);
  return {
    submissionFiles,
  };
};

const mapContentMetadata = pipe(
  getStepFields(steps.FILE_DETAILS),
  applySpec({
    title: prop("title"),
    subtitle: prop("subtitle"),
    publicationYear: prop("publicationYear"),
    publicationDate: prop("publicationDate"),
    isbn: prop("isbn"),
    volume: prop("volume"),
    edition: prop("edition"),
    keywords: prop("keywords"),
    description: prop("description"),
    descriptionText: prop("descriptionText"),
    rating: path(["rating", "name"]),
    series: prop("series"),
    episode: prop("episode"),
    subProject: prop("subProject"),
    customDropdown: prop("customDropdown"),
    customFreeform: prop("customFreeform"),
    customFreeform2: prop("customFreeform2"),
    audience: path(["audience", "name"]),
    purchaseLinks: pipe(prop(["purchaseUrl"]), ifElse(isNil, always(null), of)),
    publisher: prop("publisher"),
    vendor: prop("vendor"),
    language: prop("language"),
    licenseType: path(["licenseType", "code"]),
    ageRange: path(["ageRange", "name"]),
  }),
  pickNonEmptyFields
);

const mapInPrint = state => {
  const workType = getWorkType(state);

  if (workType.contentTypeName === "BOOK") {
    const detailsFields = getStepFields(steps.FILE_DETAILS, state);

    if (notNilOrEmpty(detailsFields.purchaseUrl)) {
      return {
        inPrint: true,
      };
    }
  } else {
    return {};
  }
};

const mapContributor = removeEmptyAndEvolve({
  role: prop("name"),
  firstName: trim,
  middleName: trim,
  lastName: trim,
});

const isWhiteSpace = test(/^\s*$/g);

const mapContributors = applySpec({
  contributors: pipe(
    getContributors,
    reject(
      where({
        firstName: either(isBlank, isWhiteSpace),
        lastName: either(isBlank, isWhiteSpace),
      })
    ),
    map(mapContributor)
  ),
});

const mapCategories = pipe(
  converge(unapply(identity), [
    getPrimaryCategory,
    getSecondaryCategory,
    getTertiaryCategory,
  ]),
  filter(notNilOrEmpty),
  map(pick(["categoryId"])),
  categories => ({ categories })
);

const mapSubmissionData = converge(unapply(mergeAll), [
  mapWorkType,
  mapProjectInfo,
  mapSubmitter,
  mapContentMetadata,
  mapInPrint,
  mapContributors,
  mapCategories,
  mapOptIns,
  mapSubmissionFiles,
]);

export default mapSubmissionData;
